'use client';

import MagicJump, { JumpTarget } from '@/components/MagicJump';
import logger from '@/utils/client-logger';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { checkPathForMp4 } from '@/utils/tools';
import Image from '@/components/Image';
import { useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

// 导入样式模块
import styles from './HomeBanner.module.css';

// 导入统一的类型定义
import type { BannerItem, BannerProps, CompleteTrackingParams } from '@/app/(home-page)/types';

// Banner信息组件
const BannerInfo = ({ item, isMobile }: { item: BannerItem; isMobile?: boolean }) => {
  if (!item.is_show_text) return null;

  // 排序文本项目 - 参考Vue项目的逻辑
  const sortedTextItems = (bannerItem: BannerItem) => {
    const typeMap = {
      '1': 'title',
      '2': 'subTitle',
      '3': 'description',
    };
    const textList = (bannerItem as any).text_list || ['1', '2', '3'];
    return textList.map((listItem: string) => ({
      type: typeMap[listItem as keyof typeof typeMap],
    }));
  };

  return (
    <div
      className={`
        banner-info absolute pointer-events-none
        ${
          isMobile
            ? 'left-1/2 w-[325px] flex flex-col items-center justify-center overflow-hidden'
            : 'overflow-hidden'
        }
      `}
      style={{
        left: isMobile ? '50%' : 'clamp(24px, 2.5vw, 48px)',
        bottom: isMobile ? '40px' : 'auto',
        top: isMobile ? 'auto' : '50%',
        transform: isMobile ? 'translate(-50%, 0)' : 'translate(0, -50%)',
        maxWidth: isMobile ? '325px' : 'clamp(400px, 41.67vw, 800px)',
      }}
    >
      {sortedTextItems(item).map((_item, _index) => {
        if (_item.type === 'title') {
          return (
            <div
              key={`title-${_index}`}
              style={{
                fontSize: isMobile ? '30px' : 'clamp(40px, 4.17vw, 80px)',
                lineHeight: isMobile ? '30px' : 'clamp(40px, 4.17vw, 80px)',
                maxHeight: isMobile ? '90px' : 'clamp(120px, 12.5vw, 240px)',
                color: item.title_color || '#000000',
                marginTop: isMobile ? '4px' : '12px', // 与Vue版本一致
                marginBottom: isMobile ? '4px' : '12px', // 与Vue版本一致
              }}
              className={`
                text-universal font-bb break-words overflow-hidden
                ${isMobile ? 'text-center first:!mt-0 last:!mb-0' : 'first:!mt-0 last:!mb-0'}
              `}
              dangerouslySetInnerHTML={{ __html: item.title || '' }}
            />
          );
        }
        if (_item.type === 'subTitle') {
          return (
            <div
              key={`subtitle-${_index}`}
              className={`
                text-universal font-bb break-words overflow-hidden
                ${isMobile ? 'text-center first:!mt-0 last:!mb-0' : 'first:!mt-0 last:!mb-0'}
              `}
              style={{
                fontSize: isMobile ? '20px' : 'clamp(20px, 2.08vw, 40px)',
                lineHeight: isMobile ? '23px' : 'clamp(23px, 2.4vw, 46px)',
                maxHeight: isMobile ? '69px' : 'clamp(69px, 7.19vw, 138px)',
                color: item.sub_title_color || '#000000',
                marginTop: isMobile ? '4px' : '12px',
                marginBottom: isMobile ? '4px' : '12px',
              }}
              dangerouslySetInnerHTML={{ __html: item.sub_title || '' }}
            />
          );
        }
        if (_item.type === 'description') {
          return (
            <div
              key={`description-${_index}`}
              className={`
                text-universal break-words overflow-hidden
                ${isMobile ? 'text-center first:!mt-0 last:!mb-0' : 'first:!mt-0 last:!mb-0'}
              `}
              style={{
                fontSize: isMobile ? '12px' : 'clamp(12px, 1.25vw, 24px)',
                lineHeight: isMobile ? '16px' : 'clamp(18px, 1.88vw, 36px)',
                maxHeight: isMobile ? '48px' : 'clamp(54px, 5.63vw, 108px)',
                color: item.description_color || '#000000',
                marginTop: isMobile ? '4px' : '12px',
                marginBottom: isMobile ? '4px' : '12px',
              }}
              dangerouslySetInnerHTML={{ __html: item.description || '' }}
            />
          );
        }
        return null;
      })}

      {item.button_text && (
        <div
          className="pointer-events-auto cursor-pointer w-fit font-bb"
          style={{
            fontSize: isMobile ? '12px' : 'clamp(12px, 0.83vw, 16px)',
            lineHeight: isMobile ? '14px' : 'clamp(14px, 0.99vw, 19px)',
            padding: isMobile ? '10px 50px' : 'clamp(12px, 0.83vw, 16px) clamp(30px, 2.6vw, 50px)',
            marginTop: isMobile ? '12px' : 'clamp(16px, 1.35vw, 26px)',
            borderRadius: isMobile ? '20px' : '25px',
            height: isMobile ? '37px' : 'auto',
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: item.button_text_color || '#FFFFFF',
            backgroundColor: item.button_color || '#000000',
          }}
        >
          {item.button_text}
        </div>
      )}
    </div>
  );
};

// Banner滑动项组件
const BannerSlide = ({
  item,
  index,
  isMobile,
  onTrack,
  onExpTrack,
  moduleIndex,
  componentId,
  moduleId,
  indexData,
  onValidExpTrack,
}: {
  item: BannerItem;
  index: number;
  isMobile: boolean;
  onTrack?: (eventParams: any) => void;
  onExpTrack?: (eventParams: any) => void;
  moduleIndex: number;
  componentId: string;
  moduleId: string | number;
  indexData: any;
  onValidExpTrack?: (eventParams: any) => void;
}) => {
  const bannerTitle = item.is_show_text
    ? (item.title || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';
  const fallbackAlt = `${indexData.layout_title || ''} TopBanner${index + 1}`
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const configuredAlt = item.alt?.trim() || '';
  const imageAlt = configuredAlt || bannerTitle || fallbackAlt;
  const ariaLabel = imageAlt;

  // 提取公共的埋点参数对象
  const trackingParams = useMemo<CompleteTrackingParams>(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: `${item.jump_type || ''}`,
      mkt_jump_content: `${item.Jump_target || ''}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${item.sku?.shop_sku || ''}`,
      business_sku: `${item.sku?.business_sku || ''}`,
      product_sku: `${item.sku?.sku || ''}`,
      product_id: `${item.sku?.id || ''}`,
      product_name: `${item.sku?.short_name || ''}`,
      group_id: `${item.product_group?.id || ''}`,
      group_name: `${item.product_group?.name || ''}`,
    }),
    [moduleIndex, componentId, moduleId, index, item],
  );

  // 曝光埋点处理
  const [bannerRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  const bannerMergedRef = useMergedRefs(bannerRef, validExposureRef);

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('Banner exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  // 处理点击埋点事件
  const handleBeforeJump = () => {
    setCampaignEntrancePageScene('首页banner', item.Jump_target);

    // 发送埋点数据
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('Banner click tracking error:', error);
      }
    }

    logger.log('Banner click tracking:', {
      index,
      item,
    });
  };

  // 创建跳转目标对象，兼容MagicJump
  const jumpTarget: JumpTarget = {
    jump_type: item.jump_type || 0,
    Jump_target: item.Jump_target || '',
    id: typeof item.id === 'string' ? item.id : String(item.id || ''),
    shop_sku: item.sku?.shop_sku,
    ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
    ...(item.product_group?.id && {
      product_group: { id: String(item.product_group.id), path: item.product_group.path },
    }),
    ...(item.crowd_recommend?.id && { crowd_recommend: { id: String(item.crowd_recommend.id) } }),
  };

  if (checkPathForMp4(item.jump_image)) {
    // 视频Banner - 使用MagicJump包装
    return (
      <div ref={bannerMergedRef}>
        <MagicJump
          prefetch
          item={jumpTarget}
          className="relative w-full h-full block"
          onBeforeJump={handleBeforeJump}
          aria-label={ariaLabel}
        >
          <video
            className={`
              block w-full bg-black
              ${isMobile ? 'h-[300px]' : 'h-full object-cover'}
            `}
            src={item.jump_image}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            controls={false}
          >
            <track kind="captions" src="banner_video_desc_en.vtt" srcLang="en" label="English" />
          </video>
          <BannerInfo item={item} isMobile={isMobile} />
        </MagicJump>
      </div>
    );
  }

  // 图片Banner - 使用MagicJump包装
  return (
    <div ref={bannerMergedRef}>
      <MagicJump
        prefetch
        item={jumpTarget}
        className="relative w-full block"
        onBeforeJump={handleBeforeJump}
        aria-label={ariaLabel}
      >
        <Image
          src={item.jump_image || ''}
          alt={imageAlt}
          width={isMobile ? 768 : 1920}
          height={isMobile ? 300 : 700}
          className="w-full block object-cover"
          priority={index === 0}
          fetchPriority={index === 0 ? 'high' : 'auto'}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw"
          style={{
            aspectRatio: item.jump_img_ratio || (isMobile ? '768/300' : '1920/700'),
          }}
        />
        <BannerInfo item={item} isMobile={isMobile} />
      </MagicJump>
    </div>
  );
};

export default function HeroBanner({
  moduleIndex,
  indexData,
  isMobile,
  priority = false,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BannerProps) {
  const swiperRef = useRef<any>(null);
  const shouldShowLayoutTitle =
    isMobile && indexData.layout_title_status === 1 && Boolean(indexData.layout_title);

  // 获取banner列表
  const bannerList = indexData?.jump_target || [];

  // 预加载第一张图片以优化LCP
  useEffect(() => {
    if (bannerList.length > 0 && bannerList[0]?.jump_image && priority) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = bannerList[0].jump_image;
      link.fetchPriority = 'high';
      document.head.appendChild(link);

      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [bannerList, priority]);

  // 如果没有数据，返回空
  if (!bannerList.length) {
    return (
      <div className="w-full h-[300px] md:h-[500px] lg:h-[600px] bg-gray-100 flex items-center justify-center" />
    );
  }

  // Swiper配置
  const swiperOptions = {
    modules: [Pagination, Autoplay, Navigation],
    loop: bannerList.length > 1,
    autoplay:
      bannerList.length > 1
        ? {
            delay: isMobile ? 3000 : 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: !isMobile, // PC端鼠标悬停时暂停
          }
        : false,
    pagination:
      bannerList.length > 1
        ? {
            clickable: true,
            dynamicBullets: false,
          }
        : false,
    observer: true,
    observeParents: true,
  };

  return (
    <div
      className={`relative w-full ${styles.heroBanner} ${isMobile ? styles.mobile : styles.desktop}`}
    >
      {shouldShowLayoutTitle && (
        <div className="px-[12px] pb-[12px] pt-[20px]">
          <h2
            className="break-words font-bb text-[20px] leading-[24px]"
            style={{ color: indexData.layout_title_color || '#000000' }}
          >
            {indexData.layout_title}
          </h2>
        </div>
      )}

      {/* Banner容器 - 有标题时按 2026 设计稿增加页边距与圆角 */}
      <div
        className={`h-auto ${
          shouldShowLayoutTitle ? 'mx-[12px] w-auto overflow-hidden rounded-[6px]' : 'w-full'
        }`}
      >
        {bannerList.length === 1 ? (
          // 单个Banner不使用轮播
          <div className="w-full h-full">
            <BannerSlide
              item={bannerList[0]}
              index={0}
              isMobile={isMobile}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              moduleIndex={moduleIndex}
              componentId={indexData.component_id || 'swipeImage'}
              moduleId={indexData.blocks_advances_id || ''}
              indexData={indexData}
              onValidExpTrack={onValidExpTrack}
            />
          </div>
        ) : (
          // 多个Banner使用轮播
          <Swiper ref={swiperRef} className="w-full h-full" {...swiperOptions}>
            {bannerList.map((item, index) => (
              <SwiperSlide key={item.id || index}>
                <BannerSlide
                  item={item}
                  index={index}
                  isMobile={isMobile}
                  onTrack={onTrack}
                  onExpTrack={onExpTrack}
                  moduleIndex={moduleIndex}
                  componentId={indexData.component_id || 'swipeImage'}
                  moduleId={indexData.blocks_advances_id || ''}
                  indexData={indexData}
                  onValidExpTrack={onValidExpTrack}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
}
